import { FileRejection, useDropzone } from "react-dropzone";
import { useCallback, useContext, useMemo, useState } from "react";
import classNames from "classnames";
import {
    MAX_FINANCIAL_DOCUMENT_FILE_SIZE_BYTES,
    MAX_FINANCIAL_DOCUMENT_FILE_SIZE_MB,
} from "../../common/constants/financial-documents";
import { useToaster } from "../general/ToastMessages/useToaster";
import { useFinancialDocumentsQuery } from "../documents/useFinancialDocumentsQuery";
import { useWorkspaceContext } from "../../state/workspaceContext";
import { Drawer } from "../general/Drawer/Drawer";
import { ImporterPageContent } from "./ImporterPageContent";
import { ImporterContext } from "./importer.context";
import { useImporterUploadMutation } from "./useImporterUpload";
import { useImporterDocumentsRefresh } from "./useImporterDocumentsRefresh";
import styles from "./ImporterPage.module.scss";
import { ImporterPageFooter } from "./ImporterPageFooter";
import { ImporterDocumentPreview } from "./ImporterDocumentPreview";
import { ImporterPreviewNavigation } from "./ImporterPreviewNavigation";
import { ImporterUploadPrompt } from "./ImporterUploadPrompt";
import { isRowReadyForImport } from "./helpers";
import { ImporterPageStatus } from "./ImporterPageStatus";
import { ImporterDocumentImportStatus } from "./types";

const errorMessage = `Only PDF or CSV files of size less than ${MAX_FINANCIAL_DOCUMENT_FILE_SIZE_MB} MB are allowed.`;

interface Props {
    onHide: () => void;
    onNext?: () => void;
}

export const Importer: React.FC<Props> = ({ onHide, onNext }) => {
    const {
        addDocumentRow,
        previewedRow,
        setPreviewedRow,
        documentRows,
        importerQueue,
    } = useContext(ImporterContext);
    const toaster = useToaster();
    const upload = useImporterUploadMutation();
    const { activeWorkspaceId } = useWorkspaceContext();
    const [initialLoadDone, setInitialLoadDone] = useState(
        documentRows.length > 0,
    );
    const rowsReadyForImport = useMemo(
        () => documentRows.filter(isRowReadyForImport),
        [documentRows],
    );
    const allDocumentsImported = useMemo(
        () =>
            documentRows.length > 0 &&
            documentRows.every(
                (row) =>
                    row.importStatus === ImporterDocumentImportStatus.FINISHED,
            ),
        [documentRows],
    );
    useImporterDocumentsRefresh();
    useFinancialDocumentsQuery({
        queryParams: {
            workspace: activeWorkspaceId ?? "",
            filters: {
                importerQueue,
            },
            limit: 0,
            page: 0,
        },
        onSuccess: (response) => {
            response.data.forEach((document) => {
                addDocumentRow({
                    id: document.id.toString(),
                    financialDocument: document,
                    integrationAccount:
                        document.integrationAccount ?? undefined,
                });
            });
            setInitialLoadDone(true);
        },
        additionalParams: {
            enabled: !initialLoadDone,
        },
    });

    const onDrop = useCallback(
        (acceptedFiles: File[], fileRejections: FileRejection[]) => {
            const timestamp = Date.now();

            for (const [index, file] of acceptedFiles.entries()) {
                const id = `${timestamp}-${index}`;
                addDocumentRow({
                    id,
                    file,
                });
                upload.mutate({
                    file,
                    documentId: id,
                });
            }

            if (fileRejections.length === 1) {
                toaster.toast(errorMessage);
            }

            if (fileRejections.length > 1) {
                toaster.toast(
                    `${fileRejections.length} files rejected. ${errorMessage}`,
                );
            }
        },
        [addDocumentRow, toaster, upload],
    );

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: ".pdf,.csv",
        maxSize: MAX_FINANCIAL_DOCUMENT_FILE_SIZE_BYTES,
    });

    const previewSibling = useCallback(
        (direction: "next" | "previous") => {
            if (!previewedRow) {
                return;
            }

            const index = documentRows.findIndex(
                (row) =>
                    row.financialDocument?.id ===
                    previewedRow.financialDocument.id,
            );
            const newIndex = direction === "next" ? index + 1 : index - 1;

            if (newIndex < 0 || newIndex >= documentRows.length) {
                return;
            }
            const newRow = documentRows[newIndex];

            if (!newRow.financialDocument || !newRow.integrationAccount) {
                return;
            }

            setPreviewedRow({
                id: newRow.id,
                financialDocument: newRow.financialDocument,
                integrationAccount: newRow.integrationAccount,
            });
        },
        [documentRows, previewedRow, setPreviewedRow],
    );

    const previewNext = useCallback(() => {
        previewSibling("next");
    }, [previewSibling]);

    const previewPrevious = useCallback(() => {
        previewSibling("previous");
    }, [previewSibling]);

    const hasFooter = rowsReadyForImport.length > 0 || allDocumentsImported;

    return (
        <>
            <div
                className={classNames(styles.container, {
                    [styles.containerWithFooter]: hasFooter,
                })}
                {...getRootProps()}
            >
                <ImporterPageStatus />
                <ImporterPageContent />
                <ImporterUploadPrompt />
                <input {...getInputProps()} />
            </div>
            <ImporterPageFooter
                rowsReadyForImport={rowsReadyForImport}
                allDocumentsImported={allDocumentsImported}
                onHide={onHide}
                onNext={onNext}
            />

            <Drawer
                open={!!previewedRow}
                direction="right"
                modal={false}
                headerAddon={
                    <ImporterPreviewNavigation
                        onNext={previewNext}
                        onPrevious={previewPrevious}
                    />
                }
                onClose={() => setPreviewedRow(undefined)}
                contentClassName={styles.drawer}
                handleOnly
            >
                {previewedRow && (
                    <ImporterDocumentPreview
                        documentRowId={previewedRow.id}
                        financialDocument={previewedRow.financialDocument}
                        integrationAccount={previewedRow.integrationAccount}
                    />
                )}
            </Drawer>
        </>
    );
};
